import React, { useState } from "react";
import { connect } from "react-redux";
import { func, array, string, object, bool } from "prop-types";
import { Button, Dropdown, Modal } from "react-bootstrap";
import AlertBanner from "components/AlertBanner";
import darkRedWarningIcon from "resources/images/alert-icon-danger.png";
import warningIcon from "resources/images/icon-16-px-warning@3x.png";
import { moveCandidatesToJob } from "../actions";

export const MoveCandidateToJob = ({
  candidateIds,
  jobId,
  jobs,
  onMoveCandidateToJob,
  handleFinishCandidateProfileAction,
  changeAction,
  hasActiveProctoredCandidates,
  isBulkAction = false,
  activeProctoredCandidates,
}) => {
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [validJob, setValidJob] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSelectedJobOdaV1Job, setIsSelectedJobOdaV1Job] = useState(false);

  const handleChangeAction = () => {
    changeAction(false, false);
  };

  const handleJobChoice = (e) => {
    const newIsSelectedJobOdaV1Job =
      jobs.get(e)?.get("customLandingPageVersion") === 1 || false;
    setSelectedJobId(e);
    setValidJob(null);
    setIsSelectedJobOdaV1Job(newIsSelectedJobOdaV1Job);
  };

  const handleMoveButton = () => {
    if (!selectedJobId) {
      setValidJob("error");
    } else {
      setShowConfirm(true);
    }
  };

  const handleConfirm = () => {
    let candidatesToMove = candidateIds;
    // exclude active proctored candidates if destination job is ODA V1
    if (isSelectedJobOdaV1Job && hasActiveProctoredCandidates) {
      candidatesToMove = candidateIds.filter(
        (candidateId) => !activeProctoredCandidates.includes(candidateId)
      );
    }

    onMoveCandidateToJob(candidatesToMove, selectedJobId, jobId);
    changeAction(false, true);
    if (handleFinishCandidateProfileAction) {
      handleFinishCandidateProfileAction();
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  let JobsDropdownOptions = [];

  if (
    jobs !== undefined &&
    jobs.filter((job) => job.get("jobStatus") > 0).size > 0
  ) {
    const sortedJobs = jobs.sort((a, b) =>
      a.get("jobTitle").localeCompare(b.get("jobTitle"))
    );

    const renderJobLocation = (job) => {
      const city = job.get("city");
      const state = job.get("state");
      const country = job.get("country");
      const jobCode = job.get("jobCode");

      // First part - city or state
      const firstPart = city || state;

      // Second part - state if city exists, otherwise country with comma if it exists
      let secondPart = "";
      if (city && state) {
        secondPart = `, ${state}`;
      } else if (country) {
        secondPart = `, ${country}`;
      }

      // Separator before job code
      const separator = city || state || country ? " - " : "";

      return (
        <div className="jobSelector-location">
          {firstPart}
          {secondPart}
          {separator} {jobCode}
        </div>
      );
    };

    JobsDropdownOptions = sortedJobs
      .filter(
        (job) =>
          job.get("jobStatus") === 2 &&
          String(job.get("jobDetailId")) !== jobId &&
          job.get("createdBy") !== 0
      )
      .entrySeq()
      .map((job) => (
        <Dropdown.Item
          eventKey={job[0]}
          key={job[0]}
          style={{
            margin: "10px 0px",
            fontSize: "16px",
            width: "300px",
          }}
        >
          <div
            className="jobSelector-job"
            style={{
              display: "block",
              whiteSpace: "nowrap",
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {job[1].get("jobTitle")}
          </div>
          <div className="jobSelector-location">
            {renderJobLocation(job[1])}
          </div>
        </Dropdown.Item>
      ));
  }

  const getMoveActiveProctoredCandidatesAlert = () => {
    if (isBulkAction) {
      return (
        <div className="mt-3">
          <AlertBanner
            alertType="warning"
            bannerColor="#FFEDCC"
            bannerText="Any candidates with active proctoring sessions will not be moved because the selected job uses a Classic landing page."
            borderStyle="1px solid #FFE3B3"
            iconInfo={{
              iconSrc: warningIcon,
              iconHeight: "16px",
              iconWidth: "16px",
            }}
            textColor="#664100"
            noMarginBottom
          />
        </div>
      );
    }
    return (
      <div className="mt-3">
        <AlertBanner
          alertType="warning"
          bannerColor="#F7CCDD"
          bannerText="This candidate has an active proctoring session and cannot be moved to a job that uses a Classic landing page."
          borderStyle="1px solid #F2B3CD"
          iconInfo={{
            iconSrc: darkRedWarningIcon,
            iconHeight: "16px",
            iconWidth: "16px",
          }}
          textColor="#550023"
          noMarginBottom
        />
      </div>
    );
  };

  return (
    <div>
      <Modal.Header>
        {showConfirm ? (
          <h4>
            Are you sure you want to move{" "}
            {candidateIds.length > 1 ? "these candidates" : "this candidate"}?
          </h4>
        ) : (
          <h4>Move to Different Job</h4>
        )}
      </Modal.Header>
      <Modal.Body>
        {showConfirm ? (
          <p>
            Once moved, the candidate will be visible in the{" "}
            <span className="font-weight-bold">Incoming</span> stage of the job
            you select. If you move the candidate to a different pipeline stage,
            any automated actions associated with that job will apply.
          </p>
        ) : (
          <div>
            <p>
              Select a job below to move the selected candidate to. The
              candidate will be visible in the{" "}
              <span className="font-weight-bold">Incoming</span> stage of the
              job you select.
            </p>
            <Dropdown onSelect={handleJobChoice} id="jobsDropdown">
              <Dropdown.Toggle variant="outline-secondary">
                {selectedJobId
                  ? jobs.get(selectedJobId).get("jobTitle")
                  : "Select a job"}
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{ maxHeight: "300px", overflowY: "scroll" }}
              >
                {JobsDropdownOptions}
              </Dropdown.Menu>
            </Dropdown>
            {validJob !== null ? (
              <p className="manual-override-invalid-feedback">
                Please choose a job.
              </p>
            ) : null}
            {hasActiveProctoredCandidates && isSelectedJobOdaV1Job
              ? getMoveActiveProctoredCandidatesAlert()
              : null}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={showConfirm ? handleCancel : handleChangeAction}
          id="cancelMoveCandidateToJob"
          variant="outline-primary"
          className="btn-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={showConfirm ? handleConfirm : handleMoveButton}
          id="confirmMoveCandidateToJob"
          disabled={
            hasActiveProctoredCandidates &&
            isSelectedJobOdaV1Job &&
            !isBulkAction
          }
        >
          Move
        </Button>
      </Modal.Footer>
    </div>
  );
};

MoveCandidateToJob.propTypes = {
  changeAction: func,
  candidateIds: array,
  jobId: string,
  jobs: object,
  onMoveCandidateToJob: func,
  handleFinishCandidateProfileAction: func,
  hasActiveProctoredCandidates: bool,
  isBulkAction: bool,
  activeProctoredCandidates: array,
};

const mapDispatchToProps = (dispatch) => ({
  onMoveCandidateToJob: (candidateIds, jobId, currentJobDetailId) =>
    dispatch(moveCandidatesToJob(candidateIds, jobId, currentJobDetailId)),
});

export default connect(undefined, mapDispatchToProps)(MoveCandidateToJob);
